/**
 * Hand-rolled single-line parser for the VybeTrader terminal DSL.
 *
 * Grammar (single line):
 *   Call    := Path "(" Args? ")"
 *   Path    := Ident ("." Ident)*
 *   Args    := Literal ("," Literal)*
 *   Literal := String | Number | "true" | "false" | "null"
 *
 * - Idents are JS-style: [A-Za-z_][A-Za-z0-9_]*
 * - Strings use single or double quotes with \\ and matching-quote escapes,
 *   plus the common escapes \n, \r, \t, \b, \f, \v, \0.
 * - Numbers are JS-decimal: optional sign, digits, optional fraction,
 *   optional exponent.
 * - Whitespace is allowed between tokens.
 * - Trailing characters after the closing ")" are an error.
 */

import { ParseError } from "./errors";
import type { DslArg, DslCall } from "./types";

const IDENT_START = /[A-Za-z_]/;
const IDENT_REST = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;

class Cursor {
  pos = 0;
  constructor(readonly src: string) {}

  eof(): boolean {
    return this.pos >= this.src.length;
  }

  peek(offset = 0): string {
    return this.src[this.pos + offset] ?? "";
  }

  advance(): string {
    return this.src[this.pos++] ?? "";
  }

  match(ch: string): boolean {
    if (this.peek() === ch) {
      this.pos++;
      return true;
    }
    return false;
  }

  skipWhitespace(): void {
    while (!this.eof()) {
      const ch = this.peek();
      if (ch === " " || ch === "\t" || ch === "\r" || ch === "\n") {
        this.pos++;
      } else {
        break;
      }
    }
  }

  error(message: string, offset = this.pos): never {
    throw new ParseError(message, offset);
  }
}

export function parse(input: string): DslCall {
  const cur = new Cursor(input);
  cur.skipWhitespace();

  const path = parsePath(cur);

  cur.skipWhitespace();
  if (!cur.match("(")) {
    cur.error(`Expected '(' after '${path.join(".")}'`);
  }

  cur.skipWhitespace();
  const args: DslArg[] = [];
  if (cur.peek() !== ")") {
    args.push(parseLiteral(cur));
    cur.skipWhitespace();
    while (cur.match(",")) {
      cur.skipWhitespace();
      args.push(parseLiteral(cur));
      cur.skipWhitespace();
    }
  }

  if (!cur.match(")")) {
    cur.error("Expected ',' or ')'");
  }

  cur.skipWhitespace();
  if (!cur.eof()) {
    cur.error(`Unexpected trailing input: '${cur.src.slice(cur.pos)}'`);
  }

  return { path, args, source: input };
}

function parsePath(cur: Cursor): string[] {
  const segments = [parseIdent(cur)];
  while (true) {
    cur.skipWhitespace();
    if (cur.peek() !== ".") break;
    cur.advance();
    cur.skipWhitespace();
    segments.push(parseIdent(cur));
  }
  return segments;
}

function parseIdent(cur: Cursor): string {
  const start = cur.pos;
  const first = cur.peek();
  if (!first || !IDENT_START.test(first)) {
    cur.error("Expected identifier");
  }
  cur.advance();
  while (!cur.eof() && IDENT_REST.test(cur.peek())) {
    cur.advance();
  }
  return cur.src.slice(start, cur.pos);
}

function parseLiteral(cur: Cursor): DslArg {
  const ch = cur.peek();
  if (ch === '"' || ch === "'") {
    return parseString(cur, ch);
  }
  if (ch === "-" || ch === "+" || DIGIT.test(ch)) {
    return parseNumber(cur);
  }
  if (IDENT_START.test(ch)) {
    return parseKeyword(cur);
  }
  cur.error(`Expected literal, got '${ch || "<eof>"}'`);
}

function parseString(cur: Cursor, quote: string): string {
  const openOffset = cur.pos;
  cur.advance();
  let out = "";
  while (!cur.eof()) {
    const ch = cur.advance();
    if (ch === quote) {
      return out;
    }
    if (ch === "\\") {
      if (cur.eof()) {
        cur.error("Unterminated escape sequence", openOffset);
      }
      const esc = cur.advance();
      switch (esc) {
        case "n":
          out += "\n";
          break;
        case "r":
          out += "\r";
          break;
        case "t":
          out += "\t";
          break;
        case "b":
          out += "\b";
          break;
        case "f":
          out += "\f";
          break;
        case "v":
          out += "\v";
          break;
        case "0":
          out += "\0";
          break;
        case "\\":
        case '"':
        case "'":
        case "/":
          out += esc;
          break;
        default:
          cur.error(`Invalid escape '\\${esc}'`, cur.pos - 1);
      }
      continue;
    }
    out += ch;
  }
  cur.error("Unterminated string literal", openOffset);
}

function parseNumber(cur: Cursor): number {
  const start = cur.pos;
  if (cur.peek() === "+" || cur.peek() === "-") {
    cur.advance();
  }
  let digits = 0;
  while (!cur.eof() && DIGIT.test(cur.peek())) {
    cur.advance();
    digits++;
  }
  if (cur.peek() === ".") {
    cur.advance();
    while (!cur.eof() && DIGIT.test(cur.peek())) {
      cur.advance();
      digits++;
    }
  }
  if (cur.peek() === "e" || cur.peek() === "E") {
    cur.advance();
    if (cur.peek() === "+" || cur.peek() === "-") {
      cur.advance();
    }
    let expDigits = 0;
    while (!cur.eof() && DIGIT.test(cur.peek())) {
      cur.advance();
      expDigits++;
    }
    if (expDigits === 0) {
      cur.error("Expected exponent digits", start);
    }
  }
  if (digits === 0) {
    cur.error("Invalid number", start);
  }
  const text = cur.src.slice(start, cur.pos);
  const value = Number(text);
  if (!Number.isFinite(value)) {
    cur.error(`Invalid numeric literal '${text}'`, start);
  }
  return value;
}

function parseKeyword(cur: Cursor): DslArg {
  const start = cur.pos;
  const ident = parseIdent(cur);
  if (ident === "true") return true;
  if (ident === "false") return false;
  if (ident === "null") return null;
  cur.error(`Unexpected identifier '${ident}' in argument position`, start);
}
